"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const stories = [{
  id: 1,
  title: "Cherry Creek Modern",
  category: "Whole Home Remodel",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670428/img/bt_remodeling_cherry_creek.png",
  ratio: 16 / 9,
  span: "md:col-span-7",
  description: "A comprehensive transformation of a mid-century residence, integrating open-concept living with high-end architectural finishes and custom millwork throughout."
}, {
  id: 2,
  title: "Hilltop Sanctuary",
  category: "Master Suite",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670428/img/bt_remodeling_hilltop_bath.png",
  ratio: 4 / 5,
  span: "md:col-span-5",
  description: "An elegant bathroom renovation featuring book-matched marble, custom lighting, and a spa-inspired layout designed for ultimate relaxation and luxury."
}, {
  id: 3,
  title: "Wash Park Estate",
  category: "Kitchen Design",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670428/img/bt_remodeling_wash_park_kitchen.png",
  ratio: 4 / 5,
  span: "md:col-span-5",
  description: "A chef-grade kitchen overhaul that balances professional functionality with sophisticated aesthetics, featuring premium appliances and bespoke cabinetry."
}, {
  id: 4,
  title: "Boulder Ridge",
  category: "Modern Addition",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670428/img/bt_remodeling_boulder_addition.png",
  ratio: 16 / 9,
  span: "md:col-span-7",
  description: "A seamless architectural addition that expands the living space while maintaining the original home's character through precision craftsmanship and design."
}];
export default function ImpactStories() {
  return <section data-section-id="223734" id="denvers-high-end-transformations" className="bg-background text-foreground py-24 md:py-32 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 max-w-2xl">
          <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter mb-6 uppercase">
            Signature Projects
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Exceptional craftsmanship meets architectural precision. Explore our 
            portfolio of high-end residential transformations across the Denver metro area.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {stories.map((story, i) => <div key={story.id} data-index={i} className={story.span}>
              <Dialog>
                <DialogTrigger className="w-full h-full text-left outline-none group block relative overflow-hidden bg-muted shadow-md">
                  <AspectRatio ratio={story.ratio} className="w-full h-full">
                    <img src={story.image} alt={story.title} className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 bg-primary/80 backdrop-blur-sm">
                      <div className="text-center transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 ease-out px-6">
                        <span className="text-secondary font-mono text-xs md:text-sm uppercase tracking-widest mb-2 block opacity-90">
                          {story.category}
                        </span>
                        <h3 className="text-2xl md:text-4xl font-default font-bold text-primary-foreground tracking-tight">
                          {story.title}
                        </h3>
                      </div>
                    </div>
                  </AspectRatio>
                </DialogTrigger>
                <DialogContent className="max-w-5xl p-0 overflow-hidden border-border bg-card rounded-md gap-0 shadow-4xl">
                  <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
                    <div className="relative h-64 md:h-full min-h-[300px]">
                      <img src={story.image} alt={story.title} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="p-8 md:p-16 flex flex-col justify-center bg-card">
                      <span className="text-secondary font-mono text-sm uppercase tracking-widest mb-6 block">
                        {story.category}
                      </span>
                      <DialogTitle className="text-3xl md:text-5xl font-default font-bold mb-6 tracking-tight text-primary">
                        {story.title}
                      </DialogTitle>
                      <DialogDescription className="text-muted-foreground text-base md:text-lg mb-10 leading-relaxed font-default">
                        {story.description}
                      </DialogDescription>
                      <Button asChild size="lg" className="w-fit rounded-md uppercase tracking-wider font-semibold group bg-primary text-primary-foreground hover:bg-secondary transition-colors">
                        <Link to="/OurPortfolioPage">
                          View Project Details
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}